'use client';

import { Button, cn, Skeleton } from '@order-up/common';
import type { Location as LocationRecord, Tenant } from '@order-up/models';
import { ArrowLeft, Home } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createLogger } from '../utils/logger';
import { useGetLocationsByTenantId } from '../hooks/useLocations';

const logger = createLogger('RestaurantSelector');

interface LocationSelectorProps {
  tenant: Tenant;
  onSelect: (locationRecord: LocationRecord) => void;
  onBack: () => void;
  title?: string;
  description?: string;
  emptyMessage?: string;
}

export function LocationSelector({
  tenant,
  onSelect,
  onBack,
  title: _title = "Select a Restaurant",
  description: _description,
  emptyMessage
}: LocationSelectorProps) {
  const { data: locations, isLoading, error } = useGetLocationsByTenantId(tenant.tenantId);
  const hasAutoSelectedRef = useRef(false);

  const _defaultDescription = `You're signing into ${tenant.displayName}. Choose a restaurant to continue.`;
  const defaultEmptyMessage = `No restaurants found for ${tenant.name}.`;

  // Auto-select if only one restaurant available
  useEffect(() => {
    if (locations && locations.length === 1 && !isLoading && !error && !hasAutoSelectedRef.current) {
      logger.debug('Auto-selecting single restaurant', { restaurantName: locations[0].name });
      hasAutoSelectedRef.current = true;
      onSelect(locations[0]);
    }
  }, [locations?.length, isLoading, error]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {/* Show 3 skeleton restaurant options while loading */}
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-linear-to-r from-card/60 to-card/40 backdrop-blur-sm border border-border/30 rounded-lg">
            <Skeleton className="size-8 sm:size-9 rounded-lg" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-3 w-2/3 rounded" />
            </div>
            <Skeleton className="h-3 w-10 rounded" />
          </div>
        ))}

        {/* Back button stays visible during loading */}
        <div className="pt-4 border-t border-border/20 mt-6">
          <Button
            variant="ghost"
            onClick={onBack}
            size="sm"
            className={cn(
              "w-full justify-center text-muted-foreground py-3 rounded-xl",
              "hover:bg-muted/30 hover:text-foreground transition-all duration-300",
              "hover:scale-105 border border-transparent hover:border-border/20"
            )}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Organizations
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="bg-linear-to-br from-destructive/10 to-destructive/5 text-destructive rounded-2xl p-4 mb-6 ring-1 ring-destructive/10 shadow-lg shadow-destructive/5">
          <Home className="h-8 w-8" />
        </div>
        <div className="text-center space-y-3 mb-8">
          <p className="font-semibold text-destructive text-lg">Error loading locations</p>
          <p className="text-sm text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-lg border border-border/50">{error.message}</p>
        </div>
        <Button
          variant="outline"
          onClick={onBack}
          size="sm"
          className={cn(
            "hover:bg-primary/5 border-primary/30 rounded-xl px-6 py-2.5",
            "transition-all duration-300 hover:scale-105 hover:shadow-sm"
          )}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!locations || locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="bg-linear-to-br from-muted/30 to-muted/10 text-muted-foreground rounded-2xl p-5 mb-6 ring-1 ring-border/20 shadow-sm">
          <Home className="h-10 w-10" />
        </div>
        <p className="text-muted-foreground text-center font-medium text-lg bg-muted/20 px-4 py-2 rounded-lg border border-border/30 mb-8">
          {emptyMessage || defaultEmptyMessage}
        </p>
        <Button
          variant="outline"
          onClick={onBack}
          size="sm"
          className={cn(
            "hover:bg-primary/5 border-primary/30 rounded-xl px-6 py-2.5",
            "transition-all duration-300 hover:scale-105 hover:shadow-sm"
          )}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {locations.map((locationRecord: LocationRecord, index: number) => (
        <Button
          key={locationRecord.restaurantId}
          variant="ghost"
          className={cn(
            "w-full justify-start h-auto group",
            "bg-linear-to-r from-card/60 to-card/40 backdrop-blur-sm",
            "border border-border/30 rounded-lg",
            "hover:bg-linear-to-r hover:from-secondary/20 hover:to-secondary/10",
            "hover:border-secondary/40 hover:shadow-md hover:shadow-secondary/5",
            "hover:scale-[1.01] active:scale-[0.99]",
            "transition-all duration-300 ease-out",
            "animate-in fade-in-50 slide-in-from-right-8",
            "p-2.5 sm:p-3"
          )}
          style={{ animationDelay: `${index * 100}ms` }}
          onClick={() => onSelect(locationRecord)}
        >
          <div className="flex items-center gap-2.5 sm:gap-3 w-full">
            <div className={cn(
              "bg-linear-to-br from-secondary/15 via-secondary/8 to-transparent",
              "text-secondary-foreground flex items-center justify-center rounded-lg",
              "ring-1 ring-secondary/15 shadow-sm shadow-secondary/5",
              "group-hover:ring-secondary/30 group-hover:shadow-secondary/10",
              "group-hover:scale-105 group-hover:-rotate-1",
              "transition-all duration-300 ease-out",
              "size-8 sm:size-9"
            )}>
              <Home className="size-3.5 sm:size-4" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <p className={cn(
                "font-semibold text-foreground leading-tight",
                "group-hover:text-secondary-foreground transition-colors duration-300",
                "text-sm truncate"
              )}>
                {locationRecord.name}
              </p>
              <p className="text-xs text-muted-foreground/80 font-normal mt-0.5 truncate">
                {locationRecord.address.street}, {locationRecord.address.city}
              </p>
            </div>
            <div className={cn(
              "opacity-0 group-hover:opacity-100 transition-all duration-300",
              "group-hover:translate-x-0.5 transform"
            )}>
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-secondary motion-safe:animate-pulse"></div>
                <div className="text-secondary-foreground font-medium text-xs">Enter</div>
              </div>
            </div>
          </div>
        </Button>
      ))}

      <div className="pt-4 border-t border-border/20 mt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          size="sm"
          className={cn(
            "w-full justify-center text-muted-foreground py-3 rounded-xl",
            "hover:bg-muted/30 hover:text-foreground transition-all duration-300",
            "hover:scale-105 border border-transparent hover:border-border/20"
          )}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
      </div>
    </div>
  );
}
